// ===== BEGIN FILE: src/components/report-v2/ReportClosingPage.tsx =====
/**
 * Report Closing Page — appears at the very end of the report.
 * Contains:
 *  1. "Continue Your Journey with ShreemAstro" app promotion
 *  2. Key app features / USPs
 *  3. 3-step registration + 7-day free trial process
 *  4. Closing Bhagavad Gita quote + signature
 */

import React from 'react';
import { Box, Typography } from '@mui/joy';
import type { ReportData } from './types';

interface ReportClosingPageProps {
  data: ReportData;
}

const BRAND = {
  purple: '#5B21B6',
  purpleLight: '#7C3AED',
  purpleSoft: '#F5F3FF',
  gold: '#F59E0B',
  goldLight: '#FEF9C3',
  maroon: '#602941',
  text: '#1F2937',
  textMuted: '#6B7280',
  border: '#E5E7EB',
  success: '#047857',
  successSoft: '#ECFDF5',
};

/** App USP features */
const APP_FEATURES = [
  {
    icon: '📅',
    title: 'Daily · Weekly · Monthly Forecasts',
    desc: 'Personalised dasha-transit predictions delivered as notifications — know your high and low energy days before they arrive.',
    accent: '#0369A1',
  },
  {
    icon: '🔮',
    title: 'Life Event Finder',
    desc: 'AI-powered probability engine scans the next 12 months to forecast when major life events — property, business, travel — are most likely.',
    accent: '#7C3AED',
  },
  {
    icon: '⏰',
    title: 'Personal Muhurta Advisor',
    desc: 'Electional astrology engine to find your most auspicious time windows for any important initiative — from surgery to Graha Pravesh.',
    accent: '#B45309',
  },
  {
    icon: '🤖',
    title: 'AI Chat Astrologer',
    desc: 'GPT-4-powered chat that knows your chart — ask anything from "Why do I struggle with relationships?" to "What does my current dasha mean?"',
    accent: BRAND.purple,
  },
  {
    icon: '👨‍👩‍👧',
    title: 'Family Chart Manager',
    desc: 'Store and analyse charts for your entire family — parents, spouse, children — in one place with relationship synastry.',
    accent: '#065F46',
  },
  {
    icon: '💎',
    title: 'Classical Yoga Detection',
    desc: '40+ Raj, Dhana, Mahapurusha and Arishta yogas detected automatically, with live status tracking through your dasha periods.',
    accent: '#9D174D',
  },
  {
    icon: '🧘',
    title: 'Karma Remedies & House Balancing',
    desc: 'Practical remedy suggestions — mantras, gemstones, colours, activities — tailored to your weakest houses and malefic placements.',
    accent: '#0F766E',
  },
  {
    icon: '📊',
    title: 'SWOT · Current Period · Detailed Analysis',
    desc: 'On-demand AI reports for your strength matrix, present dasha reading and deep planetary narrative — available anytime, for any saved profile.',
    accent: BRAND.success,
  },
];

const REGISTRATION_STEPS = [
  {
    step: '1',
    title: 'Visit shreemastro.com',
    desc: 'Open the app in any browser — no installation required. Click "Get Started" on the homepage.',
    icon: '🌐',
  },
  {
    step: '2',
    title: 'Register with your email',
    desc: 'Enter your email to receive a one-click magic link — no passwords to remember. Google sign-in is also available.',
    icon: '📧',
  },
  {
    step: '3',
    title: 'Enter birth details & explore',
    desc: 'Add your birth date, time and city. Your full Vedic chart is computed instantly. Your 7-day free trial begins automatically.',
    icon: '🚀',
  },
];

export const ReportClosingPage: React.FC<ReportClosingPageProps> = ({ data }) => {
  const firstName = data.birthDetails.name.split(' ')[0];

  return (
    <Box
      id="section-stay-proactive"
      className="report-section"
      sx={{
        background: '#FFFFFF',
        minHeight: 'unset',
        p: 2,
        // NOTE: no breakBefore here — the .report-section CSS class handles the page break.
        '@media (min-width: 600px)': {
          minHeight: '297mm',
          p: '14mm 12mm 12mm',
        },
        '@media print': {
          minHeight: 'unset',  // removing 297mm forces real content height, avoids blank overflow page
          p: '14mm 12mm 12mm',
        },
      }}
    >
      {/* ── Hero banner ── */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND.purple} 0%, #312E81 60%, ${BRAND.maroon} 100%)`,
          borderRadius: '14px',
          px: 2.5,
          py: 2,
          mb: 3,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1.5,
          '@media (min-width: 600px)': {
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: 2.5,
            px: 3,
            py: 2.5,
          },
          '@media print': {
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: 2.5,
            background: BRAND.purple,   // flat color — gradients band in PDF
            borderRadius: '6px',
          },
        }}
      >
        <Box sx={{ flexShrink: 0, textAlign: 'center' }}>
          <Box component="span" sx={{ fontSize: '2.2rem', display: 'block' }}>🕉️</Box>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ color: '#FFF', fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.3 }}>
            Continue Your Journey with ShreemAstro<Box component="sup" sx={{ fontSize: '0.6em', fontWeight: 400, verticalAlign: 'super', lineHeight: 0, color: 'rgba(255,255,255,0.85)' }}>™</Box>
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.78rem', mt: 0.5, lineHeight: 1.5 }}>
            {firstName}, this PDF is just the beginning. The live app goes far deeper — with real-time forecasts,
            AI guidance and a growing library of insights personalised to your birth chart.
          </Typography>
        </Box>
        <Box
          component="a"
          href="https://shreemastro.com"
          sx={{
            width: '100%',
            textAlign: 'center',
            background: BRAND.gold,
            color: '#1F2937',
            fontWeight: 700,
            fontSize: '0.75rem',
            px: 2,
            py: 1,
            borderRadius: '8px',
            textDecoration: 'none',
            letterSpacing: '0.02em',
            '@media (min-width: 600px)': {
              width: 'auto',
              flexShrink: 0,
              whiteSpace: 'nowrap',
            },
            '@media print': { width: 'auto', flexShrink: 0, background: BRAND.gold, color: '#1F2937', textDecoration: 'none' },
          }}
        >
          shreemastro.com →
        </Box>
      </Box>

      {/* ── App features grid ── */}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: '0.8rem',
          color: BRAND.textMuted,
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          mb: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
        }}
      >
        <Box component="span" sx={{ color: BRAND.gold }}>✦</Box>
        What Awaits You in the App
        <Box component="span" sx={{ color: BRAND.gold }}>✦</Box>
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr',
          gap: 1.25,
          mb: 3,
          '@media (min-width: 600px)': {
            gridTemplateColumns: '1fr 1fr',
          },
          '@media print': {
            gridTemplateColumns: '1fr 1fr',
          },
        }}
      >
        {APP_FEATURES.map((f, i) => (
          <Box
            key={i}
            sx={{
              border: `1px solid ${BRAND.border}`,
              borderLeft: `3px solid ${f.accent}`,
              borderRadius: '8px',
              p: 1.25,
              background: '#FAFAFA',
              '@media print': { breakInside: 'avoid', pageBreakInside: 'avoid' },
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '0.75rem',
                color: f.accent,
                display: 'flex',
                alignItems: 'center',
                gap: 0.6,
                mb: 0.4,
              }}
            >
              <span>{f.icon}</span>
              {f.title}
            </Typography>
            <Typography sx={{ fontSize: '0.67rem', lineHeight: 1.55, color: BRAND.textMuted }}>
              {f.desc}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ── 7-day trial / registration steps ── */}
      <Box
        sx={{
          background: BRAND.goldLight,
          border: `2px solid ${BRAND.gold}`,
          borderRadius: '12px',
          p: 2,
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '0.88rem',
            color: '#92400E',
            mb: 0.25,
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          🎁 Start Your 7-Day Free Trial — No Credit Card Required
        </Typography>
        <Typography sx={{ fontSize: '0.73rem', color: '#78350F', mb: 1.75 }}>
          Full access to all features, all charts, all AI tools — completely free for 7 days.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: 1.25,
            '@media (min-width: 600px)': {
              gridTemplateColumns: '1fr 1fr 1fr',
            },
            '@media print': {
              gridTemplateColumns: '1fr 1fr 1fr',
            },
          }}
        >
          {REGISTRATION_STEPS.map((s) => (
            <Box
              key={s.step}
              sx={{
                background: '#FFFFFF',
                borderRadius: '10px',
                p: 1.5,
                textAlign: 'center',
                '@media print': { breakInside: 'avoid', pageBreakInside: 'avoid' },
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${BRAND.gold}, #D97706)`,
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: '0.75rem',
                  mb: 0.75,
                }}
              >
                {s.step}
              </Box>
              <Box component="span" sx={{ display: 'block', fontSize: '1.2rem', mb: 0.5 }}>{s.icon}</Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#92400E', mb: 0.4 }}>
                {s.title}
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', lineHeight: 1.5, color: '#78350F' }}>
                {s.desc}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Divider ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2.5 }}>
        <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, transparent, ${BRAND.purple}44)` }} />
        <Box component="span" sx={{ fontSize: '1rem', color: BRAND.gold }}>✦</Box>
        <Box component="span" sx={{ fontSize: '1rem', color: BRAND.purple }}>🕉️</Box>
        <Box component="span" sx={{ fontSize: '1rem', color: BRAND.gold }}>✦</Box>
        <Box sx={{ flex: 1, height: '1px', background: `linear-gradient(90deg, ${BRAND.purple}44, transparent)` }} />
      </Box>

      {/* ── Closing philosophical note ── */}
      <Box
        sx={{
          background: BRAND.purpleSoft,
          borderRadius: '12px',
          p: 2.5,
          mb: 2,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Decorative Om watermark */}
        <Box
          component="span"
          sx={{
            position: 'absolute',
            top: '-10px',
            right: '10px',
            fontSize: '5rem',
            color: BRAND.purple,
            opacity: 0.06,
            lineHeight: 1,
            userSelect: 'none',
          }}
        >
          ॐ
        </Box>

        {/* Bhagavad Gita quote */}
        <Box
          sx={{
            borderLeft: `4px solid ${BRAND.gold}`,
            pl: 2,
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontStyle: 'italic',
              fontSize: '0.88rem',
              lineHeight: 1.75,
              color: BRAND.text,
              fontFamily: 'Georgia, serif',
            }}
          >
            "Every action leaves behind a seed. Karma is not just about reward or punishment — it is the echo of
            your intention."
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: '0.76rem', fontWeight: 600, color: '#92400E' }}>
            — the Bhagavad Gita
          </Typography>
        </Box>

        <Typography sx={{ fontSize: '0.85rem', lineHeight: 1.8, color: BRAND.text, mb: 1.5 }}>
          Thus, astrology is not a belief system — it is a{' '}
          <Box component="span" sx={{ fontWeight: 700, color: BRAND.purple }}>
            spiritual compass
          </Box>
          . It helps you navigate your journey with clarity, caution, and grace. May this analysis serve as a
          light on your path — illuminating not just the road ahead, but the extraordinary soul who is walking it.
        </Typography>

        {/* Gratitude + signature */}
        <Box
          sx={{
            background: '#FFFFFF',
            borderRadius: '10px',
            border: `1px solid ${BRAND.purple}22`,
            p: 2,
          }}
        >
          <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.75, color: BRAND.text, mb: 1.5 }}>
            Gratitude for considering <strong>ShreemAstro<sup style={{ fontSize: '0.6em', fontWeight: 400, verticalAlign: 'super', lineHeight: 0, color: 'rgba(184,48,80,0.8)' }}>™</sup></strong> as your Astrology partner. I sincerely hope this
            analysis adds value to your living — may it inspire clarity in thought, courage in action, and
            contentment in the heart. 🙏
          </Typography>

          {/* Logo + signature row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pt: 1.5,
              borderTop: `1px solid ${BRAND.border}`,
            }}
          >
            {/* Logo on the left */}
            <Box
              component="img"
              src="/Logo1.png"
              alt="ShreemAstro"
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                e.currentTarget.src = '/logo192.png';
              }}
              sx={{
                height: '64px',
                width: '64px',
                objectFit: 'contain',
                borderRadius: '10px',
                opacity: 0.93,
              }}
            />

            {/* Signature on the right */}
            <Box sx={{ textAlign: 'right' }}>
              <Box
                sx={{
                  width: '120px',
                  height: '1px',
                  background: `linear-gradient(90deg, transparent, ${BRAND.purple})`,
                  ml: 'auto',
                  mb: 0.75,
                }}
              />
              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: BRAND.purple }}>
                Abhay Tushar Joshi
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: BRAND.textMuted, mt: 0.25 }}>
                Founder, ShreemAstro<Box component="sup" sx={{ fontSize: '0.6em', fontWeight: 400, verticalAlign: 'super', lineHeight: 0, color: 'rgba(184,48,80,0.8)' }}>™</Box>
              </Typography>
              <Typography
                component="a"
                href="https://shreemastro.com"
                sx={{
                  fontSize: '0.68rem',
                  color: BRAND.purple,
                  textDecoration: 'none',
                  fontStyle: 'italic',
                  '@media print': { color: BRAND.purple, textDecoration: 'none' },
                }}
              >
                shreemastro.com
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
